import { useState } from 'react'
import WireframeLayout, { buildSidebarSections } from '@/components/WireframeLayout'
import { cn, Badge, Card, Button, KPI } from '../components/primitives'
import { SCENARIOS } from '../components/data'

function ExecView() {
  const [sel, setSel] = useState("S2");

  return (
    <div className="flex flex-col gap-6">
      <Card className="border-amber-200 bg-gradient-to-br from-amber-50/60 to-white p-6">
        <div className="text-xs font-bold text-amber-700 uppercase tracking-widest mb-4">Executive Summary — March 2025</div>
        <div className="grid grid-cols-5 gap-5">
          <div><div className="text-xs text-slate-500">Revenue (MTD)</div><div className="text-2xl font-bold text-slate-900">$342K</div><div className="text-xs text-emerald-600 font-medium">↑ 18% vs plan</div></div>
          <div><div className="text-xs text-slate-500">Gross Margin</div><div className="text-2xl font-bold text-slate-900">59.4%</div><div className="text-xs text-amber-600 font-medium">↓ 1.2pp vs target</div></div>
          <div><div className="text-xs text-slate-500">Cash Position</div><div className="text-2xl font-bold text-slate-900">$1.2M</div><div className="text-xs text-slate-400">→ Runway: 4.8mo</div></div>
          <div><div className="text-xs text-slate-500">Service Level</div><div className="text-2xl font-bold text-slate-900">95.2%</div><div className="text-xs text-emerald-600 font-medium">↑ On target</div></div>
          <div><div className="text-xs text-slate-500">Inventory Value</div><div className="text-2xl font-bold text-slate-900">$487K</div><div className="text-xs text-violet-600 font-medium">↑ 8% overstock</div></div>
        </div>
      </Card>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Strategic Boundaries</h3>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: "Min. Service Level", val: "95%", desc: "AI won't propose plans below this" },
            { label: "Weekly Cash Limit", val: "$80K", desc: "Maximum PO value per week" },
            { label: "Margin Floor", val: "50%", desc: "Promos cannot breach this gross margin" },
            { label: "Max Days of Stock", val: "90d", desc: "Overstock alerts trigger above this" },
          ].map((c, i) => (
            <Card key={i} className="p-4 flex justify-between items-center">
              <div><div className="text-sm font-semibold text-slate-900">{c.label}</div><div className="text-xs text-slate-400 mt-0.5">{c.desc}</div></div>
              <div className="flex items-center gap-2"><span className="text-lg font-bold text-amber-700">{c.val}</span><Button variant="outline" size="sm">Edit</Button></div>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Forward-Looking Scenarios</h3>
        <div className="grid grid-cols-3 gap-3">
          {SCENARIOS.map(s => (
            <Card key={s.id} className={cn("p-5 cursor-pointer transition-all", sel === s.id ? "ring-2 ring-amber-300 bg-amber-50/40" : "hover:shadow-md")} onClick={() => setSel(s.id)}>
              <div className="text-base font-bold text-slate-900 mb-1">{s.name}</div>
              <p className="text-xs text-slate-500 mb-4 leading-relaxed">{s.desc}</p>
              <div className="grid grid-cols-2 gap-2">
                <div><div className="text-xs text-slate-400 font-medium">Revenue</div><div className="text-lg font-bold text-slate-900">${s.rev}</div></div>
                <div><div className="text-xs text-slate-400 font-medium">Margin</div><div className="text-lg font-bold text-emerald-600">{s.margin}</div></div>
                <div><div className="text-xs text-slate-400 font-medium">Capital</div><div className="text-lg font-bold text-amber-600">${s.cash}</div></div>
                <div><div className="text-xs text-slate-400 font-medium">Risk</div><div className={cn("text-lg font-bold", s.risk === "Low" ? "text-emerald-600" : s.risk === "High" ? "text-red-600" : "text-amber-600")}>{s.risk}</div></div>
              </div>
              {sel === s.id && <div className="mt-4"><Button variant="amber" size="sm" className="w-full">Approve Scenario ✓</Button></div>}
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Capital Allocation</h3>
        <Card className="p-5">
          <div className="flex gap-1 mb-4 rounded-lg overflow-hidden">
            {[{ p: 35, c: "#3b82f6" }, { p: 15, c: "#8b5cf6" }, { p: 20, c: "#f97316" }, { p: 30, c: "#10b981" }].map((x, i) => (
              <div key={i} style={{ flex: x.p, height: 10, backgroundColor: x.c }} />
            ))}
          </div>
          <div className="grid grid-cols-4 gap-3">
            {[
              { l: "Inventory", a: "$420K", p: "35%", c: "#3b82f6" },
              { l: "Marketing", a: "$180K", p: "15%", c: "#8b5cf6" },
              { l: "Operations", a: "$240K", p: "20%", c: "#f97316" },
              { l: "Reserve", a: "$360K", p: "30%", c: "#10b981" },
            ].map((x, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: x.c }} />
                <div><div className="text-sm font-medium text-slate-900">{x.l}</div><div className="text-xs text-slate-400">{x.a} ({x.p})</div></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Key Risks</h3>
        <div className="flex flex-col gap-2">
          {[
            { sev: "destructive", title: "TikTok stockout risk", desc: "2 top SKUs may OOS before 3.3 sale. Revenue at risk: $89K.", act: "Escalate", clr: "#ef4444" },
            { sev: "warning", title: "Cash flow squeeze Week 2", desc: "Planned POs ($187K) exceed budget ($160K).", act: "Review", clr: "#f59e0b" },
            { sev: "info", title: "Overstock capital tie-up", desc: "$25.6K in excess SPF50 stock. Marketing push recommended.", act: "Note", clr: "#3b82f6" },
          ].map((r, i) => (
            <Card key={i} className="flex justify-between items-center p-4" style={{ borderLeft: `3px solid ${r.clr}` }}>
              <div>
                <div className="flex items-center gap-2 mb-1"><Badge variant={r.sev}>{r.sev === "destructive" ? "critical" : r.sev}</Badge><span className="text-sm font-semibold text-slate-900">{r.title}</span></div>
                <p className="text-xs text-slate-500">{r.desc}</p>
              </div>
              <Button variant="amber" size="sm">{r.act}</Button>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ role }: { role: string }) {
  return (
    <WireframeLayout
      title="Executive Dashboard"
      subtitle="Strategic boundaries & capital allocation"
      titleIcon="◆"
      titleColor="#d97706"
      sidebarLinks={buildSidebarSections(role, "dashboard")}
    >
      <ExecView />
    </WireframeLayout>
  )
}
